import type { PaginationInfo } from '@/types/common';
import type { EventInfo } from '@/types/event';
import type { LocationCoordinate } from '@/types/location';
import type {
	SearchedEventDetail,
	EventSearchQuery,
	FeaturedEventQuery,
} from '@/types/search';

interface SearchStore {
	searchedEvents: {
		data: SearchedEventDetail[];
		pagination: PaginationInfo | null;
		isLoading: boolean;
		errorMessage: string;
	};
	featuredEvents: {
		data: SearchedEventDetail[];
		pagination: PaginationInfo | null;
		isLoading: boolean;
		errorMessage: string;
	};
	currentLocation: {
		coordinates: LocationCoordinate | null;
		name: string;
		city: string;
		country: string;
		countryCode: string;
	};
}

const useSearchStore = defineStore('search', {
	state: (): SearchStore => ({
		searchedEvents: {
			data: [],
			pagination: null,
			isLoading: false,
			errorMessage: '',
		},
		featuredEvents: {
			data: [],
			pagination: null,
			isLoading: false,
			errorMessage: '',
		},
		currentLocation: {
			coordinates: null,
			name: '',
			city: '',
			country: '',
			countryCode: '',
		},
	}),

	getters: {
		searchItems: (state: SearchStore): EventInfo[] => {
			if (!state.searchedEvents.data.length) {
				return [];
			}
			return transformEventSearchResponse(state.searchedEvents.data);
		},
		featuredItems: (state: SearchStore): EventInfo[] => {
			if (!state.featuredEvents.data.length) {
				return [];
			}
			return transformEventSearchResponse(state.featuredEvents.data);
		},
	},

	actions: {
		async getEventsBySearch(query: EventSearchQuery) {
			this.searchedEvents.isLoading = true;

			const { data, errorMessage } = await getSearchEventsAPI(query);

			if (errorMessage.value) {
				this.searchedEvents.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.searchedEvents.data = data.value.data;
				this.searchedEvents.pagination = data.value.paginationInfo;
			}
			this.searchedEvents.isLoading = false;
		},

		async getFeaturedEvents(query: FeaturedEventQuery) {
			this.featuredEvents.isLoading = true;

			const { data, errorMessage } = await getFeaturedEventsAPI(query);

			if (errorMessage.value) {
				this.featuredEvents.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.featuredEvents.data = data.value.data;
				this.featuredEvents.pagination = data.value.paginationInfo;
			}
			this.featuredEvents.isLoading = false;
		},

		// Lightweight fetchers that do not mutate store state
		async fetchEventsBySearchOnce(query: EventSearchQuery) {
			return getSearchEventsAPI(query);
		},

		async fetchFeaturedEventsOnce(query: FeaturedEventQuery) {
			return getFeaturedEventsAPI(query);
		},

		resetCurrentLocation() {
			this.currentLocation = {
				coordinates: null,
				name: '',
				city: '',
				country: '',
				countryCode: '',
			};
		},

		resetSearchedEvents() {
			this.searchedEvents.data = [];
			this.searchedEvents.pagination = null;
			this.searchedEvents.isLoading = false;
			this.searchedEvents.errorMessage = '';
		},

		resetFeaturedEvents() {
			this.featuredEvents.data = [];
			this.featuredEvents.pagination = null;
			this.featuredEvents.isLoading = false;
			this.featuredEvents.errorMessage = '';
		},
	},
});

export default useSearchStore;
